import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useMatch,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { IS_PREVIEW } from "./config";
import { ClassroomProvider, useClassrooms } from "./data/ClassroomContext";
import { LessonsProvider } from "./data/LessonsContext";
import { ProgressProvider } from "./data/ProgressContext";
import AppShell from "./components/AppShell";
import ClassroomGate from "./components/ClassroomGate";
import ui from "./components/ui.module.css";
import SignIn from "./pages/SignIn";
import Today from "./pages/Today";
import SubjectLessons from "./pages/SubjectLessons";
import LessonOverview from "./pages/LessonOverview";
import LessonPlayer from "./pages/LessonPlayer";
import LessonComplete from "./pages/LessonComplete";
import TabletSetup from "./pages/TabletSetup";
import Week from "./pages/Week";
import Learners from "./pages/Learners";
import LearnerDetail from "./pages/LearnerDetail";
import Work from "./pages/Work";
import AssignmentDetail from "./pages/AssignmentDetail";
import WorkReview from "./pages/WorkReview";

const Opening = () => (
  <div className={`${ui.page} ${ui.centered}`} role="status">
    Opening your lessons…
  </div>
);

const RequireAuth = () => {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === "checking") return <Opening />;
  if (status === "signed-out") {
    return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
  }

  return (
    <ClassroomProvider key={user.id}>
      {IS_PREVIEW && (
        <p className={ui.previewBanner}>
          Preview: sample Week 7 lessons that are not yet approved for classes.
        </p>
      )}
      <Teaching />
    </ClassroomProvider>
  );
};

// Lessons and progress belong to the class being taught. Until there is one,
// Today explains why; tablet setup stays open to school administrators who
// have no class of their own.
const Teaching = () => {
  const { user } = useAuth();
  const classes = useClassrooms();
  const onTabletSetup = useMatch("/tablets");
  const { active, classrooms } = classes;

  // The first time on a device, before the teacher's classes are known.
  if (classes.status === "loading") return <Opening />;
  if (!active) {
    return onTabletSetup ? (
      <Outlet />
    ) : (
      <AppShell>
        <ClassroomGate />
      </AppShell>
    );
  }

  return (
    <ProgressProvider
      key={`${user.id}@${active.id}`}
      classroomId={active.id}
      classroomIds={classrooms.map((c) => c.id)}
      adoptLegacy={classrooms.length === 1}
    >
      <LessonsProvider>
        <Outlet />
      </LessonsProvider>
    </ProgressProvider>
  );
};

const App = () => (
  <BrowserRouter>
    <AuthProvider>
      <Routes>
        <Route path="/sign-in" element={<SignIn />} />
        <Route element={<RequireAuth />}>
          <Route element={<AppShell />}>
            <Route index element={<Today />} />
            <Route path="subject/:subject" element={<SubjectLessons />} />
            <Route path="week" element={<Week />} />
            <Route path="learners" element={<Learners />} />
            <Route path="learners/:learnerId" element={<LearnerDetail />} />
            <Route path="work" element={<Work />} />
            <Route path="work/assignments/:assignmentId" element={<AssignmentDetail />} />
            <Route path="work/:attemptId" element={<WorkReview />} />
            <Route path="lesson/:slug" element={<LessonOverview />} />
            <Route path="lesson/:slug/done" element={<LessonComplete />} />
            <Route path="tablets" element={<TabletSetup />} />
          </Route>
          <Route path="lesson/:slug/teach" element={<LessonPlayer />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  </BrowserRouter>
);

export default App;
